import type { ACF, Page, Survey as SurveyType } from 'types';
import type { CaptionedImage, ClassicCard } from '@braze/web-sdk';

import React, { CSSProperties, useEffect, useMemo, useState } from 'react';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';

/* import {
  useFeatureFlagEnabled,
  useFeatureFlagVariantKey,
  useFeatureFlagPayload,
} from 'posthog-js/react'; */

import { ButtonClose, Paragraph, ToastProvider } from '@leafwell/components';
import { useShareImages } from 'hooks/useShareImages';
import { useWordPressContext } from 'contexts/wordpress-provider';
import { useBrazeContext } from 'contexts/braze-context';
import { useEventsTracker } from 'contexts/eventsTracker';
import { request } from 'lib/rest';

import Survey from './survey';
import EmployersLinkedInTag from './employers-linkedIn-tag';
import EmployersLinkedInPageviewEmulator from './employers-linkedin-pageview-emulator';
import HeadingBody from './heading-body';
import { getPromoRibbonTheme } from './promo-ribbon-theme';
import { useUkWaitlistModal } from 'hooks/useUkWaitlistModal';
const UkWaitlistModal = dynamic(() => import('./uk-waitlist-modal'));
const Footer = dynamic(() => import('./footer'));
const Header = dynamic(() => import('./header'));

type PageWrapProps = Pick<Page, 'title' | 'seo'> & {
  pageType?: ACF['pageType'];
  children: React.ReactElement | React.ReactElement[];
};

type ContentCard = CaptionedImage | ClassicCard;

const PageWrap: React.FC<PageWrapProps> = ({
  children,
  seo,
  pageType,
  title,
}) => {
  const [contentCards, setContentCards] = useState<ContentCard[] | null>(null);
  const brazeLoaded = contentCards !== null;

  const [posthogContentCard, setPosthogContentCard] =
    useState<ContentCard | null>(null);
  const { seoSettings, languageAlternates, siteTitle, campaigns, surveys } =
    useWordPressContext();

  const [showBrazeContentCard, setShowBrazeContentCard] = useState(true);
  const { logContentCardClick, logContentCardImpressions } = useBrazeContext();

  const { locale, asPath } = useRouter();
  const { hasBrazeInitialized, fetchContentCards } = useBrazeContext();
  const { trackEvent } = useEventsTracker();

  const image = useShareImages({
    type: 'full',
    title,
  });

  /*   const showReturningCustomersCtaEnabled = useFeatureFlagEnabled(
    'website-display-cta-for-returning-costumers',
  );

  const showReturningCustomersCtaVariant = useFeatureFlagVariantKey(
    'website-display-cta-for-returning-costumers',
  );

  const showReturningCustomersCtaPayload = useFeatureFlagPayload(
    'website-display-cta-for-returning-costumers',
  ); */

  /* useEffect(() => {
    async function makePosthogCard() {
      if (typeof window === 'undefined') return;

      if (!brazeLoaded) return;

      if (
        showReturningCustomersCtaEnabled &&
        showReturningCustomersCtaVariant === 'test' &&
        showReturningCustomersCtaPayload
      ) {
        const { ClassicCard } = await import('@braze/web-sdk');
        const card: ClassicCard = new ClassicCard(
          /* id: */ //crypto.randomUUID(),
  /* viewed: */ //false,
  /* title: */ //showReturningCustomersCtaPayload['title'],
  /* imageUrl: */ //undefined,
  /* description: */ //showReturningCustomersCtaPayload['description'],
  /* created: */ //undefined,
  /* updated: */ //undefined,
  /* categories: */ //undefined,
  /* expiresAt: */ //undefined,
  /* url: */ //showReturningCustomersCtaPayload['url'],
  /* linkText: */ //showReturningCustomersCtaPayload['label'],
  /* aspectRatio: */ //undefined,
  /* extras: */ //{ ID: showReturningCustomersCtaPayload['key'] },
  /* pinned: */ //false,
  /* dismissible: */ //false,
  /* clicked: */ //false,
  /* );
        setPosthogContentCard(card);
      } else {
        setPosthogContentCard(null);
      }
    }
    makePosthogCard();
  }, [
    showReturningCustomersCtaEnabled,
    showReturningCustomersCtaVariant,
    showReturningCustomersCtaPayload,
    brazeLoaded,
  ]);*/

  useEffect(() => {
    function getContentCards() {
      setTimeout(() => {
        // Fetch content cards on component mount
        fetchContentCards(fetchedCards => {
          setContentCards(fetchedCards as ContentCard[]);
        });
      }, 1_000);
    }

    if (!hasBrazeInitialized) {
      return;
    }

    getContentCards();
    return;
  }, [hasBrazeInitialized]);

  useEffect(() => {
    if (locale === 'en') {
      if (asPath === '/') {
        trackEvent({
          event: 'website_visit_homepage',
        });
      } else if (asPath?.includes('/blog')) {
        trackEvent({
          event: 'website_visit_blog',
        });
      } else if (asPath?.includes('/medical-card')) {
        trackEvent({
          event: 'website_visit_medical_card',
        });
        // Remove query params
        const [path] = asPath.split('?');
        // Remove leading slash and split the path
        const segments = path.replace(/^\/+/, '').split('/');

        if (segments.length === 1) {
          null; // No action needed
        } else {
          trackEvent({
            event: `website_visit_medical_card_${segments[1]?.replace(
              '-',
              '_',
            )}`,
          });
        }
      } else if (asPath?.includes('/medical-conditions')) {
        trackEvent({
          event: 'website_visit_medical_conditions',
        });
      } else if (asPath?.includes('/research')) {
        trackEvent({
          event: 'website_visit_research',
        });
      } else if (asPath?.includes('/cannabis-strains')) {
        trackEvent({
          event: 'website_visit_strains',
        });
      } else if (asPath?.includes('/partner-with-leafwell')) {
        trackEvent({
          event: 'website_visit_partners',
        });
      } else if (asPath === '/about') {
        trackEvent({
          event: 'website_visit_about',
        });
      } else if (asPath === '/careers') {
        trackEvent({
          event: 'website_visit_careers',
        });
      } else if (asPath === '/contact-us') {
        trackEvent({
          event: 'website_visit_contact_us',
        });
      } else if (asPath === '/employers') {
        trackEvent({
          event: 'website_visit_employers',
        });
      } else if (asPath === '/cannabis-guidance') {
        trackEvent({
          event: 'website_visit_cannabis_guidance',
        });
      }
    } else if (locale === 'es') {
      if (asPath === '/') {
        trackEvent({
          event: 'website_visit_homepage_es',
        });
      } else if (asPath?.includes('/blog')) {
        trackEvent({
          event: 'website_visit_blog_es',
        });
      } else if (asPath?.includes('/carta-recomendacion')) {
        trackEvent({
          event: 'website_visit_medical_card_es',
        });
      } else if (asPath?.includes('/medical-conditions')) {
        trackEvent({
          event: 'website_visit_medical_conditions_es',
        });
      } else if (asPath?.includes('/investigacion')) {
        trackEvent({
          event: 'website_visit_research_es',
        });
      } else if (asPath?.includes('/cannabis-strains')) {
        trackEvent({
          event: 'website_visit_strains_es',
        });
      } else if (asPath?.includes('/cepas-de-cannabis')) {
        trackEvent({
          event: 'website_visit_strains_es',
        });
      } else if (asPath?.includes('/colabore-con-leafwell')) {
        trackEvent({
          event: 'website_visit_partners_es',
        });
      } else if (asPath === '/sobre-nosotros') {
        trackEvent({
          event: 'website_visit_about_es',
        });
      } else if (asPath === '/contactanos') {
        trackEvent({
          event: 'website_visit_contact_us_es',
        });
      } else if (asPath === '/orientacion-cannabica-personalizada') {
        trackEvent({
          event: 'website_visit_cannabis_guidance_es',
        });
      }
    }
  }, [asPath, locale]);

  const router = useRouter();
  const { query, isReady } = router;
  const [isPartner, setIsPartner] = useState<boolean>(false);

  useEffect(() => {
    if (!isReady) return;

    if (query.utm_source && pageType === 'card') {
      request(`/partner?slug=${query.utm_source}`).then(res => {
        setIsPartner(res.data?.totalItems > 0);
      });
    } else {
      setIsPartner(false);
    }
  }, [query, pageType]);

  let cardObj: ContentCard = null;
  let showCampaign = false;

  if (brazeLoaded) {
    if (isPartner) {
      cardObj = null;
      showCampaign = false;
    } else {
      if (campaigns?.length > 0 && contentCards?.length > 0) {
        for (let i = 0; i < campaigns?.length; i++) {
          const { contentCardId, showContentCard, exceptions, specificPages } =
            campaigns[i];

          cardObj = contentCards?.find(
            card => card.extras.ID === contentCardId,
          );

          if (
            exceptions?.length > 0 &&
            exceptions.some(exception => asPath.includes(exception.slug))
          ) {
            cardObj = null;
            showCampaign = false;
            break;
          }

          if (showContentCard?.includes('homepage') && asPath === '/') {
            showCampaign = true;
          } else {
            showCampaign = showContentCard?.some(path =>
              asPath?.includes(path),
            );
          }

          if (
            specificPages?.length > 0 &&
            specificPages.some(page => asPath.includes(page.slug))
          ) {
            showCampaign = true;
          }

          if (cardObj && showCampaign) {
            logContentCardImpressions([cardObj]);
            // break the loop, we found a match
            break;
          } else if (posthogContentCard) {
            logContentCardImpressions([posthogContentCard]);
            // braze content cards have priority over posthog content cards
            cardObj = posthogContentCard;
            showCampaign = true;
          } else {
            cardObj = null;
            showCampaign = false;
          }
        }
      } else if (posthogContentCard) {
        // braze content cards have priority over posthog content cards
        cardObj = posthogContentCard;
        showCampaign = true;
      } else {
        cardObj = null;
        showCampaign = false;
      }
    }
  }

  const { showSurvey, surveyCta } = useMemo(() => {
    let matchedSurvey: SurveyType | null = null;
    let shouldShow = false;

    if (surveys && surveys.survey && surveys.survey.length > 0) {
      for (let i = 0; i < surveys.survey.length; i++) {
        const { displayConditions } = surveys.survey[i];
        const groupOfPages = displayConditions?.groupOfPages || [];
        const specificUrls = displayConditions?.specificUrls || [];

        // Check if this survey was already submitted
        const hasAlreadySubmitted =
          typeof window !== 'undefined' &&
          localStorage.getItem(
            `survey_submitted_${surveys.survey[i].surveyId}`,
          ) === 'true';

        if (hasAlreadySubmitted) {
          continue; // Skip to the next survey
        }

        if (groupOfPages.length > 0) {
          if (groupOfPages.includes('homepage') && asPath === '/') {
            matchedSurvey = surveys.survey[i];
            shouldShow = true;
          } else if (groupOfPages.some(path => asPath.includes(path))) {
            matchedSurvey = surveys.survey[i];
            shouldShow = true;
          }
        } else if (specificUrls.length > 0) {
          if (specificUrls.some(({ url }) => asPath.includes(url))) {
            matchedSurvey = surveys.survey[i];
            shouldShow = true;
          }
        }

        if (matchedSurvey && shouldShow) break;
      }
    }

    return {
      showSurvey: shouldShow,
      surveyCta: matchedSurvey,
    };
  }, [surveys, asPath]);

  const [surveyVisible, setSurveyVisible] = useState(false);

  useEffect(() => {
    if (showSurvey && surveyCta) {
      const timeout = setTimeout(() => {
        setSurveyVisible(true);
      }, 2000); // 2 seconds

      return () => clearTimeout(timeout);
    }
  }, [showSurvey, surveyCta]);

  const theme = getPromoRibbonTheme(cardObj?.extras?.theme);

  const {
    isOpen: ukModalOpen,
    dismiss: dismissUkModal,
    markSeen: markUkModalSeen,
  } = useUkWaitlistModal();

  return (
    <>
      <EmployersLinkedInTag />
      <EmployersLinkedInPageviewEmulator />
      <NextSeo
        key={`page-seo-${seo?.canonical}`}
        title={seo?.title ?? siteTitle}
        description={seo?.description}
        openGraph={{
          type: 'website',
          title: seo?.opengraph?.title,
          description: seo?.opengraph?.description,
          images: [image],
          url: seo?.canonical,
          site_name: siteTitle,
          locale,
        }}
        nofollow={seo?.metaRobotsNofollow || false}
        noindex={seo?.metaRobotsNoindex || false}
        robotsProps={{ noarchive: seo?.metaRobotsNoarchive || false }}
        canonical={seo?.canonical}
        twitter={{
          cardType: seoSettings?.social?.twitter?.cardType,
          handle: seoSettings?.social?.twitter?.authorFallback,
          site: seoSettings?.social?.twitter?.profile,
        }}
        facebook={{ appId: seoSettings?.social?.facebook?.appId }}
        languageAlternates={languageAlternates}
      />
      <ToastProvider>
        <div id="header-container" className="sticky top-0 z-[15] w-full">
          {cardObj ? (
            <div
              className={[
                'relative w-full',
                'bg-[var(--promo-bg)] text-[var(--promo-fg)]',
                'bg-repeat bg-[length:var(--promo-size-m)] bg-[position:var(--promo-pos-m)] md:bg-[length:var(--promo-size-d)] md:bg-[position:var(--promo-pos-d)]',
                'flex items-center md:justify-center gap-x-10',
                'transition-all duration-500',
                showBrazeContentCard
                  ? 'px-4 py-2 md:py-4 lg:px-10 xl:mx-auto'
                  : 'max-h-0 opacity-0 overflow-hidden -z-10',
              ].join(' ')}
              style={
                {
                  '--promo-bg': theme.bg,
                  '--promo-fg': theme.text,
                  '--promo-btn-bg': theme.buttonBg,
                  '--promo-btn-fg': theme.buttonText,
                  '--promo-btn-bg-hover': theme.buttonBgHover,
                  '--promo-size-m': theme.patternMobile.size,
                  '--promo-pos-m': theme.patternMobile.position,
                  '--promo-size-d': theme.patternDesktop.size,
                  '--promo-pos-d': theme.patternDesktop.position,
                  ...(showBrazeContentCard && theme.pattern
                    ? { backgroundImage: `url(${theme.pattern})` }
                    : {}),
                } as CSSProperties
              }
            >
              {showBrazeContentCard ? (
                <>
                  <span className="flex flex-col gap-y-0.5 min-w-0">
                    <HeadingBody
                      title={cardObj?.title}
                      level="6"
                      tagName="h2"
                      className="!text-lg md:!text-xl font-extrabold"
                    />
                    {cardObj?.description ? (
                      <Paragraph
                        size="xsmall"
                        className="!text-xs md:!text-sm font-medium"
                        dangerouslySetInnerHTML={{
                          __html: cardObj?.description,
                        }}
                      />
                    ) : null}
                  </span>

                  {cardObj?.url ? (
                    <a
                      href={cardObj.url}
                      className="relative select-none cursor-pointer whitespace-nowrap inline-grid items-center justify-center text-center text-base md:text-lg font-bold outline-none leading-none rounded-md px-4 py-3 w-fit transition-colors bg-[var(--promo-btn-bg)] text-[var(--promo-btn-fg)] hover:bg-[var(--promo-btn-bg-hover)]"
                      onClick={() => {
                        logContentCardClick(cardObj);
                        trackEvent({
                          event: 'select_content',
                          content_type: cardObj?.title,
                          item_id: cardObj?.extras?.ID,
                          origin: 'content_card',
                        });
                      }}
                    >
                      {cardObj?.linkText}
                    </a>
                  ) : null}

                  <ButtonClose
                    onClick={() => setShowBrazeContentCard(false)}
                    className="absolute right-4 lg:right-8 top-1/2 -translate-y-1/2"
                    variant={theme.closeVariant}
                    size={12}
                  />
                </>
              ) : null}
            </div>
          ) : null}
          {pageType !== 'google_my_business' &&
            pageType !== 'vpd_landing' &&
            pageType !== 'webinar_landing' &&
            pageType !== 'affiliate_landing' && (
              <Header
                pageType={pageType}
                showContentCard={
                  cardObj !== null &&
                  cardObj !== undefined &&
                  showBrazeContentCard
                }
                setShowBrazeContentCard={setShowBrazeContentCard}
              />
            )}
        </div>
        <main className="grow xl:pt-10 relative">{children}</main>
        {pageType !== 'google_my_business' && (
          <Footer
            showNavFooter={
              pageType !== 'affiliate_landing' &&
              pageType !== 'vpd_landing' &&
              pageType !== 'webinar_landing'
            }
          />
        )}
        {ukModalOpen && (
          <UkWaitlistModal
            onDismiss={dismissUkModal}
            markSeen={markUkModalSeen}
          />
        )}
        {surveyVisible && <Survey {...surveyCta} />}
      </ToastProvider>
    </>
  );
};

export default PageWrap;
